// Favourite cities (docs/APP_REFERENCE.md "Favourites"): a heart beside the city's name on its title card (or K) keeps
// the city among the visitor's favourites. A favourite is a city, not a look: Rome's Trevi and Colosseum share one
// heart. The city picker's Favourites chip shows them alone, and the Tour menu's Favourites only has the city tour go
// through them alone, every few minutes or following the night, once there are FAV_MIN of them (with fewer, every
// city, as without it). Kept in the browser (lofi-cities:favourites, the ids in picker order) and, signed in, in the
// account like the other settings (SYNCED in site/js/app.js; the worker keeps up to 1 KB of a setting's JSON, which
// is every city's id about 95 cities long). The logic is pure and tested in tests/favourites.test.mjs; app.js shows it.

export const FAV_MIN = 2;                 // favourites a favourites-only tour needs: one city alone isn't a tour
export const FAV_FILTER = 'favourites';   // the picker's Favourites chip, in a region's place (and its analytics value)

/** The kept favourites, cleaned: known city ids only, each once, in `ids`' order (the picker's). */
export function cleanFavourites(stored, ids) {
  if (!Array.isArray(stored)) return [];
  const kept = new Set(stored.filter(id => typeof id === 'string'));
  return ids.filter(id => kept.has(id));
}

/** `favs` with `id` hearted, or no longer if it was (a copy, in `ids`' order). */
export const flipFavourite = (favs, id, ids) => cleanFavourites(favs.includes(id) ? favs.filter(f => f !== id) : [...favs, id], ids);

/** Does the tour go through the favourites alone: Favourites only is on (`only`) and there are FAV_MIN of them. */
export const favouritesTour = (favs, only) => only && favs.length >= FAV_MIN;

/** The cities a tour goes through: the favourites in a favourites tour, else every city (`ids` itself). */
export const tourPool = (favs, ids, only) => (favouritesTour(favs, only) ? favs : ids);

/**
 * The next city (`step` 1) or the one before (-1) in `pool`, round and round, from `current`. A city outside the
 * pool (picked by hand) goes on to the pool's nearest one that way in `ids`' order, as ← → would pass it.
 */
export function stepPool(pool, ids, current, step = 1) {
  if (!pool.length) return null;
  const i = pool.indexOf(current);
  if (i >= 0) return pool[(((i + step) % pool.length) + pool.length) % pool.length];
  const at = ids.indexOf(current);
  return step > 0 ? pool.find(id => ids.indexOf(id) > at) ?? pool[0] : pool.findLast(id => ids.indexOf(id) < at) ?? pool.at(-1);
}

/** The visitor's favourites over the page's store (`read`, `write`), for the registry's `ids`. */
export function createFavourites({ ids, read, write }) {
  let list = cleanFavourites(read(), ids);
  const save = next => { list = next; write(list); return list; };
  return {
    get list() { return list; },
    has: id => list.includes(id),
    /** Hearts the city, or no longer: true when it's a favourite now. */
    toggle: id => save(flipFavourite(list, id, ids)).includes(id),
    /** The account's favourites, in place of this device's. */
    set: v => save(cleanFavourites(v, ids)),
  };
}

// The site's 7×7 pixel heart (index.html's #i-heart): its outline, and the whole heart that fills it in.
const HEART_LINE = 'M1 0h2v1H1zM4 0h2v1H4zM0 1h1v1H0zM3 1h1v1H3zM6 1h1v1H6zM0 2h1v2H0zM6 2h1v2H6zM1 4h1v1H1zM5 4h1v1H5zM2 5h1v1H2zM4 5h1v1H4zM3 6h1v1H3z';
const HEART_FILL = 'M1 0h2v1H1zM4 0h2v1H4zM0 1h7v3H0zM1 4h5v1H1zM2 5h3v1H2zM3 6h1v1H3z';
/** The heart icon: an outline (.heart-line) with the whole heart under it (.heart-fill), which CSS fills in when on. */
export const heartSvg = () => `<svg class="heart" viewBox="0 0 7 7" aria-hidden="true"><path class="heart-fill" d="${HEART_FILL}"/><path class="heart-line" d="${HEART_LINE}"/></svg>`;

/** The title card's heart for the city on screen (`name`), pressed while it's a favourite. */
export function heartLabel(name, on) {
  return { label: `Favourite ${name}`, title: on ? `${name} is a favourite · remove it (K)` : `Add ${name} to your favourites (K)` };
}

/** The Tour menu's Favourites only line under its name: what it tours, or what it waits for. */
export function tourFavsNote(count) {
  if (count >= FAV_MIN) return `only your ${count} hearted cities`;
  return count === 1 ? 'heart one more city to tour them' : `heart ${FAV_MIN} or more cities to tour them`;
}

/** The toast as a city is hearted (`on`) or no longer, `count` the favourites now. */
export function heartToast(name, on, count) {
  if (!on) return `${name} is no longer a favourite`;
  if (count < FAV_MIN) return `${name} is a favourite · heart one more to tour your favourites`;
  return `${name} is a favourite · ${count} hearted cities`;
}

/** The toast as Favourites only is switched (`on`), `count` the favourites now. */
export function tourFavsToast(on, count) {
  if (!on) return 'City tour · every city';
  return count >= FAV_MIN ? `City tour · only your ${count} favourites` : `Favourites only · heart ${FAV_MIN} or more cities; until then the tour goes everywhere`;
}
